/*
 * FICHIER: useStrapi.js
 * BUT: Hook personnalisé React pour gérer les données Strapi
 * 
 * Ce hook offre une interface simplifiée pour :
 * - Récupérer des données depuis l'API Strapi
 * - Gérer les états de chargement et d'erreur automatiquement
 * - Offrir une fonction refetch pour recharger les données
 * - Adapter les différents types de collections Strapi
 * 
 * Un hook est une fonction React qui permet de "brancher" de la logique 
 * d'état et d'effets dans un composant fonctionnel.
 */

// Import des hooks React de base
import { useState, useEffect } from 'react';

// Import de toutes les fonctions API Strapi
import * as strapiApi from '../api/strapi';

// Import du client API générique
import apiClient from '../api/strapi'; // Import de l'instance axios par défaut

/**
 * Hook personnalisé pour récupérer des données depuis Strapi
 * 
 * COMMENT UTILISER CE HOOK :
 * const { data, loading, error, refetch } = useStrapi('actualites');
 * 
 * @param {string} collection - Le nom de la collection Strapi (ex: 'actualites', 'clubs')
 * @param {Object} options - Options supplémentaires (non utilisées actuellement)
 * @returns {Object} - Un objet contenant :
 *   - data: Les données récupérées (tableau ou objet)
 *   - loading: Boolean indiquant si la requête est en cours
 *   - error: Objet d'erreur si quelque chose s'est mal passé
 *   - refetch: Fonction pour relancer la récupération des données
 */
const useStrapi = (collection, _options = {}) => {
  // ÉTAT LOCAL DU HOOK
  // useState permet de gérer l'état dans un composant fonctionnel
  
  const [data, setData] = useState([]);           // Données récupérées (commence par tableau vide)
  const [loading, setLoading] = useState(true);   // État de chargement (commence par true)
  const [error, setError] = useState(null);       // Erreur éventuelle (commence par null)
  const [refetchIndex, setRefetchIndex] = useState(0); // Index pour forcer un nouveau chargement

  // Fonction pour déclencher un rechargement des données
  // Quand on appelle refetch(), on incrémente refetchIndex, ce qui déclenche useEffect
  const refetch = () => setRefetchIndex(prev => prev + 1);

  // EFFET DE BORD - se déclenche quand collection ou refetchIndex change
  useEffect(() => {
    // Fonction asynchrone pour récupérer les données
    const fetchData = async () => {
      setLoading(true);  // On commence le chargement
      
      try {
        let result; // Variable pour stocker le résultat
        
        // SWITCH CASE - choisir la bonne fonction API selon la collection
        // Chaque collection a potentiellement sa propre logique de récupération
        switch (collection) {
          case 'actualites':
            result = await strapiApi.getActualites();
            break;
          case 'evenements':
            result = await strapiApi.getEvenements();
            break;
          case 'clubs':
            result = await strapiApi.getClubs();
            break;
          case 'bibliotheques':
            result = await strapiApi.getBibliotheque();
            break;
          case 'livres':
            result = await strapiApi.getLivres();
            console.log('🔍 useStrapi - Résultat de getLivres():', result);
            break;
          case 'sports':
            result = await strapiApi.getSports();
            break;
          case 'club-fidelites':
            result = await strapiApi.getPartenaires();
            break;
          case 'partenaires':
            result = await strapiApi.getPartenairesData();
            break;
          case 'sondages':
            // TRAITEMENT SPÉCIAL pour les sondages (structure de réponse variable)
            const sondagesResponse = await strapiApi.getSondages();
            
            // Vérifier explicitement si la réponse est vide ou nulle
            if (!sondagesResponse) {
              result = [];
            }
            // Adapter le traitement en fonction de la structure réelle
            else if (sondagesResponse && sondagesResponse.sondages) {
              // Si la réponse est au format { sondages: [...], error: null }
              result = sondagesResponse.sondages;
            } else if (Array.isArray(sondagesResponse)) {
              // Si la réponse est directement un tableau
              result = sondagesResponse;
            } else {
              // Format inconnu - on retourne un tableau vide
              result = [];
            }
            break;
          case 'accueil':
            // SINGLE TYPE - structure différente des collections
            // Un single type ne retourne qu'un seul objet, pas un tableau
            const accueilResponse = await apiClient.get(`/${collection}?populate=*`);
            result = accueilResponse.data.data; // Pour single type, data contient directement l'objet
            break;
          default:
            // CAS GÉNÉRIQUE - pour les collections qu'on n'a pas spécifiquement traitées
            const response = await apiClient.get(`/${collection}?populate=*`);
            result = response.data.data;
        }
        
        // MISE À JOUR DE L'ÉTAT
        setData(result || []);  // Met les données (ou tableau vide si result est null)
        setError(null);         // Pas d'erreur
      } catch (err) {
        // EN CAS D'ERREUR
        setError(err);          // Stocke l'erreur
        setData([]);            // Données vides
      } finally {
        // DANS TOUS LES CAS (succès ou échec)
        setLoading(false);      // Arrête le chargement
      }
    };

    fetchData(); // Exécute la fonction
  }, [collection, refetchIndex]); // Dépendances : se relance si collection ou refetchIndex change

  // RETOUR DU HOOK
  // Le hook retourne un objet avec toutes les données et fonctions utiles
  return { data, loading, error, refetch };
};

/**
 * Hook pour récupérer un élément spécifique par son ID
 * 
 * UTILISATION :
 * const { data, loading, error, refetch } = useStrapiById('actualites', 123);
 * 
 * @param {string} collection - Nom de la collection
 * @param {number|string} id - ID de l'élément à récupérer
 */
export const useStrapiById = (collection, id) => {
  // États similaires au hook principal
  const [data, setData] = useState(null);     // Un seul élément, donc null par défaut
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refetchIndex, setRefetchIndex] = useState(0);

  // Fonction refetch identique
  const refetch = () => setRefetchIndex(prev => prev + 1);

  useEffect(() => {
    const fetchData = async () => {
      // Si pas d'ID, on ne fait rien
      if (!id) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // Utilise la fonction getItemById de l'API
        const result = await strapiApi.getItemById(collection, id);
        setData(result);
        setError(null);
      } catch (err) {
        console.error(`Erreur lors de la récupération de ${collection}/${id}:`, err);
        setError(err);
        setData(null);  // null pour un élément unique non trouvé
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [collection, id, refetchIndex]); // Dépendances incluent aussi l'id

  return { data, loading, error, refetch };
};

// Export par défaut du hook principal
export default useStrapi; 